import { useState, useEffect } from "react";
import { Modal, FormField, Input, Button } from "../ui/index";
import { cascadeAPI } from "../../api/services";

const plans   = ["Starter", "Professional", "Premium"];
const cycles  = ["Mensuel", "Trimestriel", "Annuel"];
const methodes = ["Carte bancaire", "Virement bancaire", "Prélèvement SEPA"];

const prixParPlan = { Starter: 49, Professional: 269, Premium: 1999 };
const today = new Date().toLocaleDateString("fr-FR").split("/").join("/");
const selectStyle = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40";

export default function AddAbonnementModal({ isOpen, onClose, onAdd }) {
  const [form, setForm] = useState({
    revendeur: "", client: "", projet: "", plan: "Professional", cycle: "Mensuel",
    prix: "269", dateDebut: today, methode: "Carte bancaire",
  });

  // Données dynamiques cascade
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projets, setProjets] = useState([]);

  // Charger revendeurs au montage
  useEffect(() => {
    if (!isOpen) return;
    cascadeAPI.revendeurs().then(res => setRevendeurs(res.data.data || res.data || [])).catch(console.error);
  }, [isOpen]);

  // Charger clients quand revendeur change
  useEffect(() => {
    if (!form.revendeur) { setClients([]); return; }
    cascadeAPI.clients(form.revendeur).then(res => setClients(res.data.data || res.data || [])).catch(console.error);
  }, [form.revendeur]);

  // Charger projets quand client change
  useEffect(() => {
    if (!form.client) { setProjets([]); return; }
    cascadeAPI.projets(form.client, form.revendeur).then(res => setProjets(res.data.data || res.data || [])).catch(console.error);
  }, [form.client, form.revendeur]);

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    if (field === "revendeur") setForm(f => ({ ...f, revendeur: val, client: "", projet: "" }));
    else if (field === "client") setForm(f => ({ ...f, client: val, projet: "" }));
    else if (field === "plan") setForm(f => ({ ...f, plan: val, prix: String(prixParPlan[val] || "") }));
    else setForm(f => ({ ...f, [field]: val }));
  };

  const handleSubmit = () => {
    if (!form.revendeur || !form.client || !form.projet || !form.plan) return;
    const payload = { ...form, prochainPaiement: form.dateDebut };
    onAdd?.(payload);
    setForm({ revendeur: "", client: "", projet: "", plan: "Professional", cycle: "Mensuel", prix: "269", dateDebut: today, methode: "Carte bancaire" });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ajouter un abonnement" subtitle="Créez un nouvel abonnement pour un projet.">
      <div className="space-y-4">
        {/* Cascade: Revendeur / Client / Projet */}
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Revendeur" required>
            <select value={form.revendeur} onChange={handleChange("revendeur")} className={selectStyle}>
              <option value="">Sélectionner</option>
              {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Client" required>
            <select value={form.client} onChange={handleChange("client")} disabled={!form.revendeur} className={selectStyle}>
              <option value="">Sélectionner</option>
              {clients.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Projet" required>
            <select value={form.projet} onChange={handleChange("projet")} disabled={!form.client} className={selectStyle}>
              <option value="">Sélectionner</option>
              {projets.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
            </select>
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Plan" required>
            <select value={form.plan} onChange={handleChange("plan")} className={selectStyle}>
              {plans.map(p => <option key={p}>{p}</option>)}
            </select>
          </FormField>
          <FormField label="Cycle de facturation" required>
            <select value={form.cycle} onChange={handleChange("cycle")} className={selectStyle}>
              {cycles.map(c => <option key={c}>{c}</option>)}
            </select>
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Prix (EUR)" required>
            <Input value={form.prix} onChange={handleChange("prix")} type="number" />
          </FormField>
          <FormField label="Date de début" required>
            <Input value={form.dateDebut} onChange={handleChange("dateDebut")} />
          </FormField>
        </div>

        <FormField label="Méthode de paiement" required>
          <select value={form.methode} onChange={handleChange("methode")} className={selectStyle}>
            {methodes.map(m => <option key={m}>{m}</option>)}
          </select>
        </FormField>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit}>Ajouter</Button>
        </div>
      </div>
    </Modal>
  );
}
